import React, { useState } from 'react';
import { BookOpen, CheckCircle, ExternalLink, Clock, Paperclip, Eye, FileText, ChevronRight, Check } from 'lucide-react';
import FileViewer from './FileViewer';

const TrainingViewer = ({ content, onComplete, isCompleted, lastSubmissionCancelled, previousResponse, isAdmin = false, children }) => {
    const [training, setTraining] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) return content;
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing training content:", e);
            return { sections: [] };
        }
    });

    const [completedSections, setCompletedSections] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return new Set(parsed.completedSections || []);
            } catch (e) { return new Set(); }
        }
        return new Set();
    });

    const [activeSection, setActiveSection] = useState(0);

    React.useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setCompletedSections(new Set(parsed.completedSections || []));
            } catch (e) { setCompletedSections(new Set()); }
        } else {
            setCompletedSections(new Set());
            setActiveSection(0);
        }
    }, [previousResponse]);

    const [previewFile, setPreviewFile] = useState(null);

    const handleSectionComplete = (sectionIndex) => {
        setCompletedSections(prev => new Set([...prev, sectionIndex]));
    };

    const handleCompleteTraining = () => {
        onComplete(JSON.stringify({
            completedSections: Array.from(completedSections),
            totalSections: training.sections.length,
            completedAt: new Date().toISOString()
        }));
    };

    const allSectionsComplete = completedSections.size === training.sections.length;
    const progressPercent = training.sections.length > 0
        ? Math.round((completedSections.size / training.sections.length) * 100)
        : 0;

    if (!training.sections || training.sections.length === 0) {
        return (
            <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">
                No training content available
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Progress */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">
                    {completedSections.size} of {training.sections.length} completed
                </span>
                <span className="text-gray-500 dark:text-slate-400 font-medium">{progressPercent}%</span>
            </div>

            {/* Section List */}
            <div className="space-y-2">
                {training.sections.map((section, index) => {
                    const isSectionDone = completedSections.has(index);
                    const isActive = activeSection === index;

                    return (
                        <div key={index}>
                            {/* Section Header */}
                            <button
                                onClick={() => setActiveSection(index)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                                    isActive
                                        ? 'border-blue-200 dark:border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/5'
                                        : isSectionDone
                                            ? 'border-green-200 dark:border-green-500/20 bg-green-50/30 dark:bg-green-500/5'
                                            : 'border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-600'
                                }`}
                            >
                                {/* Status indicator */}
                                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSectionDone
                                        ? 'border-green-400 dark:border-green-500 bg-green-400 dark:bg-green-500'
                                        : isActive
                                            ? 'border-blue-400 dark:border-blue-500 bg-blue-400 dark:bg-blue-500'
                                            : 'border-gray-300 dark:border-slate-500'
                                }`}>
                                    {isSectionDone ? (
                                        <Check className="w-3.5 h-3.5 text-white" />
                                    ) : isActive ? (
                                        <span className="text-xs font-bold text-white">{index + 1}</span>
                                    ) : (
                                        <span className="text-xs font-semibold text-gray-400 dark:text-slate-500">{index + 1}</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${
                                        isSectionDone ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'
                                    }`}>
                                        {section.title || `Section ${index + 1}`}
                                    </p>
                                    {section.duration && (
                                        <p className="text-xs text-gray-400 dark:text-slate-500">{section.duration} min</p>
                                    )}
                                </div>
                                {isActive && <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-500 rotate-90" />}
                            </button>

                            {/* Active Section Content (inline expand) */}
                            {isActive && (
                                <div className="mt-2 px-4 py-4 border border-gray-100 dark:border-slate-700/40 rounded-xl bg-gray-50/50 dark:bg-slate-800/30 space-y-4">
                                    {/* Content */}
                                    <div className="prose dark:prose-invert prose-sm max-w-none text-gray-700 dark:text-slate-300">
                                        <div className="whitespace-pre-wrap leading-relaxed">
                                            {section.content}
                                        </div>
                                    </div>

                                    {/* Resources */}
                                    {section.resources && section.resources.length > 0 && (
                                        <div className="space-y-1.5">
                                            <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Resources</p>
                                            {section.resources.map((resource, rIndex) => (
                                                <a
                                                    key={rIndex}
                                                    href={resource.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-500/30 transition-colors text-sm text-gray-700 dark:text-slate-300"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                                                    <span className="truncate">{resource.title || resource.url}</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}

                                    {/* Attachments */}
                                    {section.attachments && section.attachments.length > 0 && (
                                        <div className="space-y-1.5">
                                            <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Attachments</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {section.attachments.map((file, aIndex) => (
                                                    <button
                                                        key={aIndex}
                                                        onClick={() => setPreviewFile(file)}
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/30 rounded-md text-xs text-gray-600 dark:text-slate-400 transition-colors"
                                                    >
                                                        <FileText className="w-3 h-3" />
                                                        <span className="truncate max-w-[140px]">{file.file_name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Mark Complete / Navigation */}
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                                                disabled={activeSection === 0}
                                                className="px-3 py-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Previous
                                            </button>
                                            <button
                                                onClick={() => setActiveSection(Math.min(training.sections.length - 1, activeSection + 1))}
                                                disabled={activeSection === training.sections.length - 1}
                                                className="px-3 py-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Next
                                            </button>
                                        </div>
                                        {!isSectionDone && !isCompleted && !isAdmin && (
                                            <button
                                                onClick={() => handleSectionComplete(activeSection)}
                                                className="px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                                Mark Done
                                            </button>
                                        )}
                                        {isSectionDone && (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 font-medium">
                                                <Check className="w-3.5 h-3.5" /> Completed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Children */}
            {!isCompleted && !isAdmin && children}

            {/* Complete Training */}
            {allSectionsComplete && !isCompleted && !isAdmin && (
                <div className="flex justify-end pt-2">
                    <button
                        onClick={handleCompleteTraining}
                        className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        Complete Training
                    </button>
                </div>
            )}

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default TrainingViewer;
